import { Howl, Howler } from "howler";
import { createElement, createIcons, Disc3, Heart, Pause, Play, SkipBack, SkipForward, ThumbsDown } from "lucide";
import "./styles.css";

createIcons({
  icons: {
    Disc3,
    Heart,
    Pause,
    Play,
    SkipBack,
    SkipForward,
    ThumbsDown
  }
});

const SEGMENT_SECONDS = 30;
const STORAGE_KEYS = {
  liked: "blindbox-liked-tracks",
  disliked: "blindbox-disliked-tracks"
} as const;

type Track = {
  id: string;
  key: string;
  title: string;
  url: string;
  size: number;
  format: string;
  uploaded?: string;
};

type Segment = {
  track: Track;
  start: number;
  end: number;
  full: boolean;
};

type TracksResponse = {
  tracks: Track[];
};

const elements = {
  signalState: byId("signalState"),
  nowLabel: byId("nowLabel"),
  trackTitle: byId("trackTitle"),
  trackMeta: byId("trackMeta"),
  elapsedTime: byId("elapsedTime"),
  remainingTime: byId("remainingTime"),
  progressFill: byId("progressFill"),
  orbitScan: byId("orbitScan"),
  prevButton: byId<HTMLButtonElement>("prevButton"),
  playButton: byId<HTMLButtonElement>("playButton"),
  nextButton: byId<HTMLButtonElement>("nextButton"),
  likeButton: byId<HTMLButtonElement>("likeButton"),
  dislikeButton: byId<HTMLButtonElement>("dislikeButton"),
  fullButton: byId<HTMLButtonElement>("fullButton"),
  sky: byId<HTMLCanvasElement>("sky")
};

let tracks: Track[] = [];
let sound: Howl | null = null;
let current: Segment | null = null;
let isPlaying = false;
let isLoading = false;
let rafId = 0;
let history: Segment[] = [];
let likedTracks = readSet(STORAGE_KEYS.liked);
let dislikedTracks = readSet(STORAGE_KEYS.disliked);

Howler.autoSuspend = false;

initSky();
bindEvents();
void loadTracks();

function byId<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) {
    throw new Error(`Missing element: ${id}`);
  }
  return element as T;
}

function bindEvents() {
  elements.playButton.addEventListener("click", () => {
    if (isPlaying) {
      pause();
      return;
    }
    void playOrStart();
  });

  elements.nextButton.addEventListener("click", () => {
    void nextSegment();
  });

  elements.prevButton.addEventListener("click", () => {
    void previousSegment();
  });

  elements.fullButton.addEventListener("click", () => {
    void toggleFullSong();
  });

  elements.likeButton.addEventListener("click", () => {
    if (!current) return;
    togglePreference(current.track.key, likedTracks, dislikedTracks, STORAGE_KEYS.liked, STORAGE_KEYS.disliked);
    renderPreference();
  });

  elements.dislikeButton.addEventListener("click", () => {
    if (!current) return;
    togglePreference(current.track.key, dislikedTracks, likedTracks, STORAGE_KEYS.disliked, STORAGE_KEYS.liked);
    renderPreference();
    if (dislikedTracks.has(current.track.key)) {
      void nextSegment();
    }
  });
}

async function loadTracks() {
  setStatus("寻找中");
  try {
    const response = await fetch("/api/tracks", {
      headers: { accept: "application/json" }
    });
    if (!response.ok) {
      throw new Error(`Track request failed: ${response.status}`);
    }
    const data = (await response.json()) as TracksResponse;
    tracks = data.tracks;

    if (tracks.length === 0) {
      setEmptyState();
      return;
    }

    setStatus(`${tracks.length} 首可听`);
    elements.nowLabel.textContent = "音乐已经就绪";
    elements.trackTitle.textContent = "点继续抽取片段";
    elements.trackMeta.textContent = "会随机落在一首歌的 30 秒里";
    renderPreference();
  } catch {
    setStatus("未连上");
    elements.nowLabel.textContent = "暂时没有听到音乐";
    elements.trackTitle.textContent = "稍后再试一次";
    elements.trackMeta.textContent = "刷新页面后会重新寻找歌曲";
  }
}

async function playOrStart() {
  if (current && sound) {
    play();
    return;
  }
  await nextSegment({ skipHistory: true });
}

async function nextSegment(options: { skipHistory?: boolean } = {}) {
  if (isLoading || tracks.length === 0) return;
  const track = chooseTrack();
  if (!track) return;

  if (current && !options.skipHistory) {
    history.push(current);
    history = history.slice(-24);
  }

  await loadTrack(track, { autoplay: true, full: false });
}

async function previousSegment() {
  if (isLoading || history.length === 0) return;
  const segment = history.pop();
  if (!segment) return;
  await loadTrack(segment.track, {
    autoplay: true,
    full: segment.full,
    forcedStart: segment.start
  });
}

async function toggleFullSong() {
  if (!current || !sound) {
    const track = chooseTrack();
    if (!track) return;
    await loadTrack(track, { autoplay: true, full: true });
    return;
  }

  const duration = safeDuration(sound.duration());
  const wasFullSong = current.full;
  const nextStart = wasFullSong ? randomStart(duration) : 0;

  current = {
    ...current,
    full: !wasFullSong,
    start: nextStart,
    end: wasFullSong ? Math.min(nextStart + SEGMENT_SECONDS, duration) : duration
  };

  sound.seek(current.start);
  play();

  render();
}

async function loadTrack(
  track: Track,
  options: { autoplay: boolean; full: boolean; forcedStart?: number }
) {
  isLoading = true;
  setStatus("载入中");
  stopFrame();
  if (sound) {
    sound.unload();
  }

  await new Promise<void>((resolve) => {
    sound = new Howl({
      src: [track.url],
      format: [track.format],
      html5: true,
      preload: "metadata",
      onload: () => {
        const duration = safeDuration(sound?.duration() ?? 0);
        const start = options.forcedStart ?? (options.full ? 0 : randomStart(duration));
        current = {
          track,
          start,
          end: options.full ? duration : Math.min(start + SEGMENT_SECONDS, duration),
          full: options.full
        };
        sound?.seek(start);
        isLoading = false;
        render();
        if (options.autoplay) play();
        resolve();
      },
      onloaderror: () => {
        isLoading = false;
        setStatus("载入失败");
        elements.nowLabel.textContent = "这首暂时无法播放";
        elements.trackTitle.textContent = track.title;
        elements.trackMeta.textContent = "换一段试试";
        resolve();
      },
      onend: () => {
        void nextSegment();
      }
    });
  });
}

function chooseTrack() {
  const available = tracks.filter((track) => !dislikedTracks.has(track.key));
  const pool = available.length > 0 ? available : tracks;
  if (pool.length === 0) return null;

  if (pool.length === 1) return pool[0];

  let next = pool[Math.floor(Math.random() * pool.length)];
  if (current && next.key === current.track.key) {
    next = pool[(pool.indexOf(next) + 1) % pool.length];
  }
  return next;
}

function play() {
  if (!sound || !current) return;
  sound.play();
  isPlaying = true;
  setStatus(current.full ? "整首播放" : "片段播放");
  renderPlayButton();
  startFrame();
}

function pause() {
  if (!sound) return;
  sound.pause();
  isPlaying = false;
  setStatus("已暂停");
  renderPlayButton();
  stopFrame();
}

function render() {
  if (!current || !sound) return;
  const duration = safeDuration(sound.duration());
  const position = Number(sound.seek()) || current.start;
  const segmentEnd = current.full ? duration : current.end;
  const range = Math.max(segmentEnd - current.start, 1);
  const progress = clamp((position - current.start) / range, 0, 1);

  elements.nowLabel.textContent = current.full ? "正在播放整首" : "正在播放片段";
  elements.trackTitle.textContent = current.track.title;
  elements.trackMeta.textContent = current.full
    ? `${formatTime(position)} / ${formatTime(duration)} · ${current.track.format.toUpperCase()}`
    : `${formatTime(current.start)} - ${formatTime(current.end)} · ${current.track.format.toUpperCase()}`;
  elements.elapsedTime.textContent = formatTime(Math.max(position - current.start, 0));
  elements.remainingTime.textContent = current.full
    ? formatTime(Math.max(duration - position, 0))
    : formatTime(Math.max(current.end - position, 0));
  elements.progressFill.style.transform = `scaleX(${progress})`;
  elements.orbitScan.style.setProperty("--scan", `${Math.round(progress * 100)}%`);
  elements.fullButton.classList.toggle("is-active", current.full);
  elements.fullButton.querySelector("span")!.textContent = current.full ? "回到片段" : "播放整首";
  renderPlayButton();
  renderPreference();
}

function startFrame() {
  stopFrame();
  const step = () => {
    if (!sound || !current || !isPlaying) return;
    const position = Number(sound.seek()) || current.start;
    if (!current.full && position >= current.end - 0.08) {
      void nextSegment();
      return;
    }
    render();
    rafId = window.requestAnimationFrame(step);
  };
  rafId = window.requestAnimationFrame(step);
}

function stopFrame() {
  if (rafId) {
    window.cancelAnimationFrame(rafId);
    rafId = 0;
  }
}

function renderPlayButton() {
  elements.playButton.setAttribute("aria-label", isPlaying ? "暂停" : "继续播放");
  const icon = elements.playButton.querySelector("svg");
  const text = elements.playButton.querySelector("span");
  if (icon) icon.replaceWith(createElement(isPlaying ? Pause : Play));
  if (text) text.textContent = isPlaying ? "暂停" : "继续";
}

function renderPreference() {
  const key = current?.track.key;
  elements.likeButton.classList.toggle("is-active", Boolean(key && likedTracks.has(key)));
  elements.dislikeButton.classList.toggle("is-active", Boolean(key && dislikedTracks.has(key)));
}

function togglePreference(
  key: string,
  target: Set<string>,
  opposite: Set<string>,
  targetStorageKey: string,
  oppositeStorageKey: string
) {
  if (target.has(key)) {
    target.delete(key);
  } else {
    target.add(key);
    opposite.delete(key);
  }
  writeSet(targetStorageKey, target);
  writeSet(oppositeStorageKey, opposite);
}

function setEmptyState() {
  setStatus("没有歌曲");
  elements.nowLabel.textContent = "暂时没有可播放内容";
  elements.trackTitle.textContent = "上传 MP3 或 Opus 后再来听";
  elements.trackMeta.textContent = "支持 .mp3、.opus、.ogg";
}

function setStatus(value: string) {
  elements.signalState.textContent = value;
}

function randomStart(duration: number) {
  if (!Number.isFinite(duration) || duration <= SEGMENT_SECONDS + 2) {
    return 0;
  }
  return Math.floor(Math.random() * Math.max(duration - SEGMENT_SECONDS, 1));
}

function safeDuration(duration: number) {
  return Number.isFinite(duration) && duration > 0 ? duration : SEGMENT_SECONDS;
}

function formatTime(seconds: number) {
  const value = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(value / 60);
  const remainingSeconds = value % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
}

function readSet(key: string) {
  try {
    return new Set(JSON.parse(localStorage.getItem(key) ?? "[]") as string[]);
  } catch {
    return new Set<string>();
  }
}

function writeSet(key: string, value: Set<string>) {
  localStorage.setItem(key, JSON.stringify([...value]));
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function initSky() {
  const canvas = elements.sky;
  const context = canvas.getContext("2d");
  if (!context) return;

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const stars = Array.from({ length: 130 }, () => ({
    x: Math.random(),
    y: Math.random(),
    r: Math.random() * 1.4 + 0.25,
    tone: Math.random(),
    drift: Math.random() * 0.12 + 0.02
  }));

  const resize = () => {
    const scale = window.devicePixelRatio || 1;
    canvas.width = Math.floor(window.innerWidth * scale);
    canvas.height = Math.floor(window.innerHeight * scale);
    canvas.style.width = `${window.innerWidth}px`;
    canvas.style.height = `${window.innerHeight}px`;
    context.setTransform(scale, 0, 0, scale, 0, 0);
  };

  const draw = (time = 0) => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    context.clearRect(0, 0, width, height);

    const gradient = context.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "#08080b");
    gradient.addColorStop(0.42, "#101018");
    gradient.addColorStop(1, "#16100f");
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);

    context.strokeStyle = "rgba(176, 241, 220, 0.09)";
    context.lineWidth = 1;
    for (let i = 0; i < 7; i += 1) {
      const y = height * (0.14 + i * 0.13);
      context.beginPath();
      context.moveTo(0, y);
      context.bezierCurveTo(width * 0.28, y - 22, width * 0.72, y + 22, width, y - 8);
      context.stroke();
    }

    for (const star of stars) {
      const drift = reducedMotion ? 0 : Math.sin(time * 0.00012 * star.drift + star.x * 9) * 8;
      const x = star.x * width + drift;
      const y = star.y * height;
      const alpha = 0.36 + Math.sin(time * 0.001 + star.tone * 8) * 0.18;
      context.fillStyle = star.tone > 0.82 ? `rgba(255, 191, 128, ${alpha})` : `rgba(232, 255, 247, ${alpha})`;
      context.beginPath();
      context.arc(x, y, star.r, 0, Math.PI * 2);
      context.fill();
    }

    if (!reducedMotion) {
      window.requestAnimationFrame(draw);
    }
  };

  resize();
  window.addEventListener("resize", resize);
  draw();
}
